"use client";
import { useMemo } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import { useGetArticleDetail, useGetArticleNeighbors, useGetHotArticles } from "@/lib/api/hooks";
import { useI18n, getLocalizedField } from "@/i18n";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatViewCount = (count) => {
  if (count == null) return "0";
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
};

const estimateReadingTime = (text) => {
  if (!text) return 3;
  const plain = text.replace(/<[^>]*>/g, "").replace(/\s+/g, "");
  return Math.max(1, Math.ceil(plain.length / 500));
};

const sanitizeHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "");
};

const getTitle = (a, lang) => getLocalizedField(a, "title", lang) || "";
const getCategoryName = (a, lang) => getLocalizedField(a, "categoryName", lang) || "";

/* ---------- Skeleton ---------- */
const DetailSkeleton = () => (
  <>
    <div className="container" style={{ padding: "32px 0 72px" }}>
      <div className="skeleton" style={{ width: 180, height: 13, marginBottom: 20 }} />
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 32 }}>
        <div style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 14, padding: "40px 48px 44px" }}>
          <div className="skeleton sk-block" style={{ height: 40, marginBottom: 10 }} />
          <div className="skeleton sk-block" style={{ height: 40, marginBottom: 20, maxWidth: "75%" }} />
          <div className="skeleton" style={{ width: 220, height: 14, marginBottom: 24 }} />
          <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 24, marginBottom: 32 }}>
            <div className="skeleton sk-block" style={{ aspectRatio: "16/9", marginBottom: 16, borderRadius: 8 }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="skeleton" style={{ width: 200, height: 12 }} />
            </div>
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton sk-block" style={{ height: 18, marginBottom: 14, maxWidth: i === 7 ? "65%" : "100%" }} />
          ))}
        </div>

        <aside>
          <div style={{ marginBottom: 24 }}>
            <div className="skeleton" style={{ width: 100, height: 14, marginBottom: 8 }} />
            <div style={{ width: 60, height: 3, background: "var(--color-border)", borderRadius: 2 }} />
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: "flex", gap: 12, paddingBottom: 16, marginBottom: 16, borderBottom: i < 2 ? "1px solid var(--color-border)" : "none" }}>
              <div className="skeleton sk-block" style={{ width: 96, height: 64, flexShrink: 0, borderRadius: 6 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="skeleton sk-block" style={{ height: 13, marginBottom: 6, maxWidth: "95%" }} />
                <div className="skeleton sk-block" style={{ height: 13, marginBottom: 6, maxWidth: "80%" }} />
                <div className="skeleton" style={{ width: 60, height: 11 }} />
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  </>
);

/* ---------- Article Body ---------- */
const ArticleBody = ({ content, hasHtml }) => {
  if (hasHtml) {
    return (
      <div className="blog-article-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
    );
  }
  const paragraphs = useMemo(() => {
    if (!content) return [];
    return content.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  }, [content]);
  return (
    <div className="blog-article-body">
      {paragraphs.map((p, i) => <p key={i}>{p.replace(/\n/g, " ")}</p>)}
    </div>
  );
};

/* ---------- Neighbor Card ---------- */
const NeighborCard = ({ neighbor, direction }) => {
  const { t, lang } = useI18n();
  if (!neighbor) return null;
  const title = getTitle(neighbor, lang);
  return (
    <Link
      href={`/blog/${neighbor.id}`}
      style={{
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        padding: "20px 24px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: 12,
        transition: "all 220ms cubic-bezier(0.16,1,0.3,1)",
        borderColor: "var(--color-border)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--color-brand)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(83,201,6,0.10)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--color-border)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-text-muted)", marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 8 }}>
        {direction === "prev" && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="14" height="14"><path d="m15 18-6-6 6-6"/></svg>
        )}
        {direction === "prev" ? t("blog.prevArticle") : t("blog.nextArticle")}
        {direction === "next" && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="14" height="14" style={{ marginLeft: 6 }}><path d="m9 18 6-6-6-6"/></svg>
        )}
      </div>
      <div style={{
        fontSize: 14,
        fontWeight: 600,
        color: "var(--color-text)",
        lineHeight: 1.5,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}>
        {title}
      </div>
    </Link>
  );
};

/* ---------- Sidebar Hot Item ---------- */
const HotItem = ({ article, rank }) => {
  const { lang } = useI18n();
  const title = getTitle(article, lang);
  const categoryName = getCategoryName(article, lang);
  return (
    <Link
      href={`/blog/${article.id}`}
      style={{
        display: "flex",
        gap: 12,
        paddingBottom: 16,
        marginBottom: 16,
        borderBottom: "1px solid var(--color-border)",
        textDecoration: "none",
        transition: "opacity 180ms",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.75"; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
    >
      {/* Thumbnail */}
      <div style={{ flexShrink: 0, width: 96, height: 64, borderRadius: 6, overflow: "hidden", background: "var(--color-bg-alt)", position: "relative" }}>
        {article.coverImage && (
          <img src={article.coverImage} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        )}
        {/* Rank number overlay */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 22,
          height: 22,
          background: rank <= 3 ? "var(--color-brand)" : "rgba(0,0,0,0.55)",
          color: "#fff",
          fontSize: 11,
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottomRightRadius: 6,
        }}>{rank}</div>
      </div>
      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.45,
          color: "var(--color-text)",
          marginBottom: 6,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>{title}</div>
        <div style={{ fontSize: 11.5, color: "var(--color-text-muted)", display: "flex", alignItems: "center", gap: 8 }}>
          <span>📅 {formatDate(article.publishTime)}</span>
          {categoryName && <span style={{ color: "var(--color-brand)", fontWeight: 600 }}>· {categoryName}</span>}
        </div>
      </div>
    </Link>
  );
};

export default function BlogDetailPage() {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const { data, isLoading, error } = useGetArticleDetail(id);
  const article = data?.data ?? data ?? null;

  const neighborParams = article?.id
    ? { articleId: article.id, categoryId: article.categoryId }
    : { articleId: null, categoryId: null };
  const { data: neighborsData } = useGetArticleNeighbors(neighborParams);

  const { data: hotData } = useGetHotArticles();
  const hotArticles = (hotData?.data ?? hotData ?? []).slice(0, 3);

  if (isLoading) return <DetailSkeleton />;

  if (error || !article || !article.id) {
    return (
      <div className="container" style={{ padding: "100px 0", textAlign: "center" }}>
        <div style={{ fontSize: 16, color: "var(--color-text-muted)", marginBottom: 24 }}>{t("blog.notFound")}</div>
        <Link href="/blog" className="btn btn-primary" style={{ display: "inline-flex" }}>{t("blog.backToList")}</Link>
      </div>
    );
  }

  const title = getTitle(article, lang);
  const content = getLocalizedField(article, "content", lang) || "";
  const hasHtml = /<[a-z][\s\S]*>/i.test(content);
  const readingTime = estimateReadingTime(content);
  const neighborPrev = neighborsData?.prev ?? null;
  const neighborNext = neighborsData?.next ?? null;

  return (
    <div className="container" style={{ padding: "32px 0 80px" }}>
      {/* ------ Breadcrumb (page-level, outside card) ------ */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12.5,
        color: "var(--color-text-muted)",
        marginBottom: 20,
        whiteSpace: "nowrap",
      }}>
        <Link href="/" style={{ color: "var(--color-text-muted)", textDecoration: "none", transition: "color 180ms" }} onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-brand)"; }} onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}>{t("common.home")}</Link>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="12" height="12" style={{ opacity: 0.55 }}><path d="m15 18-6-6 6-6"/></svg>
        <Link href="/blog" style={{ color: "var(--color-text-muted)", textDecoration: "none", transition: "color 180ms" }} onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-brand)"; }} onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}>{t("nav.blog")}</Link>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="12" height="12" style={{ opacity: 0.55 }}><path d="m15 18-6-6 6-6"/></svg>
        <span style={{ color: "var(--color-text-secondary)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>{title}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 32 }}>
        {/* ==================== MAIN ARTICLE — WHITE CARD ==================== */}
        <main style={{ minWidth: 0 }}>
          <div style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 14,
            padding: "40px 48px 44px",
          }}>

            {/* ------ Title block ------ */}
            <header style={{ marginBottom: 24 }}>
          <h1
            style={{
              fontSize: 42,
              lineHeight: 1.22,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "var(--color-text)",
              margin: "0 0 16px",
            }}
          >
            {title}
          </h1>

          {/* Meta row — dots separator like BBC */}
          <div style={{
            fontSize: 13.5,
            color: "var(--color-text-muted)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 10,
          }}>
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "var(--color-text-secondary)",
              fontWeight: 600,
            }}>
              <span style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: "linear-gradient(135deg, var(--color-brand), var(--color-brand-dark))",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}>G</span>
              GuineeMall
            </span>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--color-border)" }} />
            <span>📅 {formatDate(article.publishTime)}</span>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--color-border)" }} />
            <span>⏱ {t("blog.readingTime", { minutes: readingTime })}</span>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--color-border)" }} />
            <span>👁 {formatViewCount(article.viewCount)} {t("blog.viewUnit")}</span>
          </div>
        </header>

        {/* Thin divider */}
        <div style={{ borderTop: "1px solid var(--color-border)", marginBottom: 28 }} />

        {/* ------ Cover image with caption ------ */}
        {article.coverImage && (
          <figure style={{ margin: "0 0 32px" }}>
            <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--color-border)", background: "var(--color-bg-alt)" }}>
              <img
                src={article.coverImage}
                alt={title}
                style={{ width: "100%", height: "auto", display: "block", maxHeight: 440, objectFit: "cover" }}
              />
            </div>
            <figcaption style={{
              marginTop: 10,
              textAlign: "center",
              fontSize: 12,
              color: "var(--color-text-muted)",
              fontStyle: "italic",
            }}>
              {title}
            </figcaption>
          </figure>
        )}

        {/* ------ Article body ------ */}
        <article>
          <ArticleBody content={content} hasHtml={hasHtml} />
        </article>

        {/* ------ Prev / Next ------ */}
        {(neighborPrev || neighborNext) && (
          <div style={{
            marginTop: 56,
            paddingTop: 32,
            borderTop: "1px solid var(--color-border)",
            display: "grid",
            gridTemplateColumns: neighborPrev && neighborNext ? "1fr 1fr" : "1fr",
            gap: 14,
          }}>
            {neighborPrev && <NeighborCard neighbor={neighborPrev} direction="prev" />}
            {neighborNext && <NeighborCard neighbor={neighborNext} direction="next" />}
          </div>
        )}
          </div>
        </main>

        {/* ==================== SIDEBAR — HOT ARTICLES ==================== */}
        <aside style={{ position: "sticky", top: 96, alignSelf: "start" }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--color-text-secondary)",
            marginBottom: 8,
            display: "inline-block",
          }}>
            {t("blog.hotArticles")}
          </div>
          <div style={{ width: 42, height: 3, background: "var(--color-brand)", borderRadius: 2 }} />
        </div>

        {hotArticles.length > 0 ? (
          hotArticles.map((h, idx) => (
            <HotItem key={h.id} article={h} rank={idx + 1} />
          ))
        ) : (
          <div style={{ fontSize: 13, color: "var(--color-text-muted)", padding: "20px 0" }}>{t("blog.noHotArticles")}</div>
        )}

        {/* Back to blog list */}
        <Link
          href="/blog"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            width: "100%",
            padding: "12px 16px",
            marginTop: 8,
            fontSize: 13,
            fontWeight: 600,
            color: "var(--color-text-secondary)",
            textDecoration: "none",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 10,
            transition: "all 200ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--color-brand)";
            e.currentTarget.style.color = "var(--color-brand)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "var(--color-border)";
            e.currentTarget.style.color = "var(--color-text-secondary)";
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="14" height="14">
            <path d="m15 18-6-6 6-6"/>
          </svg>
          {t("blog.viewAllArticles")}
        </Link>
        </aside>
      </div>
    </div>
  );
}
